import { Controller } from '@hotwired/stimulus'

// Swaps one section of the tester dashboard preview between the copy as testers
// read it and the rich text editor for that copy.
//
// The editor is only hidden, never detached, so its Trix input still submits with
// the rest of the form — a section left un-edited round-trips its existing value
// rather than being blanked.
//
// Closing the editor repaints the preview from what is in the editor now, not from
// what was last saved, so the card always shows what you are about to publish.
export default class extends Controller {
  static targets = ['preview', 'editor', 'button', 'reset', 'defaultLabel', 'unsaved']
  static values = { default: String }

  connect() {
    this.editing = false
    this.initialValue = this.editorValue()
  }

  toggle() {
    this.editing = !this.editing

    if (!this.editing) this.renderPreview()

    this.previewTarget.classList.toggle('hidden', this.editing)
    this.editorTarget.classList.toggle('hidden', !this.editing)
    this.buttonTarget.textContent = this.editing ? 'Done' : 'Edit'
    this.buttonTarget.setAttribute('aria-expanded', this.editing)

    // Reset is only meaningful while editing, and only when there is something to
    // reset away from.
    this.refreshResetButton()

    if (this.editing) this.trixElement()?.focus()
  }

  reset() {
    this.trixElement()?.editor.loadHTML(this.defaultValue)
    this.markDirty()
    this.refreshResetButton()
  }

  // Fires on every keystroke in Trix, so the "unsaved" flag appears as soon as the
  // section actually differs from what is stored.
  markDirty() {
    if (this.hasUnsavedTarget) {
      this.unsavedTarget.classList.toggle(
        'hidden',
        this.editorValue() === this.initialValue
      )
    }
    this.refreshResetButton()
  }

  refreshResetButton() {
    if (!this.hasResetTarget) return
    const show = this.editing && !this.isDefault(this.editorValue())
    this.resetTarget.classList.toggle('hidden', !show)
  }

  renderPreview() {
    const value = this.editorValue()

    if (this.plainText(value) === '' && !this.hasAttachment(value)) {
      // A blank field falls back to the default on the dashboard, so preview that
      // rather than an empty card.
      this.previewTarget.innerHTML = this.defaultValue
    } else {
      // ActionText wraps stored copy in .trix-content; match it so lists and links
      // pick up the same styles they will have once saved.
      const wrapper = document.createElement('div')
      wrapper.className = 'trix-content'
      wrapper.innerHTML = value
      this.previewTarget.replaceChildren(wrapper)
    }

    if (this.hasDefaultLabelTarget) {
      this.defaultLabelTarget.classList.toggle('hidden', !this.isDefault(value))
    }
  }

  // Compared as text, not markup: Trix rewrites the HTML it round-trips, so copy
  // nobody has touched still will not match the default string byte for byte.
  isDefault(html) {
    const text = this.plainText(html)
    return text === '' || text === this.plainText(this.defaultValue)
  }

  plainText(html) {
    const probe = document.createElement('div')
    probe.innerHTML = html
    return probe.textContent.replace(/\s+/g, ' ').trim()
  }

  hasAttachment(html) {
    const probe = document.createElement('div')
    probe.innerHTML = html
    return !!probe.querySelector('img, figure, action-text-attachment')
  }

  trixElement() {
    return this.editorTarget.querySelector('trix-editor')
  }

  editorValue() {
    return this.trixElement()?.value ?? ''
  }
}
